import { useState } from 'react';
import { ProductPriceType } from '@setvi/shared/interfaces';
import { Box, Divider, withStyles } from '@material-ui/core';

import { ProductLabel, PromotionBadge } from 'componentsV2';
import { TextColor } from '@setvi/shared/enums';
import { PriceButton, Stock } from 'pages/products/components';
import {
  RoundCheckboxSelected,
  RoundCheckboxUnselect
} from '@setvi/shared/components/sicons';
import {
  SButton,
  SCheckbox,
  SETVIImage,
  SHighlightText,
  SText
} from '@setvi/shared/components';
import { ListPrice } from 'pages/products/components/products-list/components/product/components';
import { AIProductMatch, MobileGridFullPrice, Statuses } from './components';
import { MobileProductCardProps } from '../list';
import { MobileCard } from '../styled';

const BoxWithMargin = withStyles(({ spacing }) => ({
  root: {
    marginTop: spacing(2)
  }
}))(Box);

export const MobileProductGridCard = ({
  product,
  priceType,
  filters,
  gridConfig,
  aiSearchActive,
  productPricesLoading,
  searchQuery,
  checked,
  style,
  onProductClick,
  onPromotionClick,
  onGetProductPrice,
  onStockClick,
  handleCheck,
  checkboxDisabled
}: MobileProductCardProps) => {
  const [singlePriceLoading, setSinglePriceLoading] = useState(false);

  return (
    <MobileCard style={style} onClick={() => onProductClick(product)}>
      <SETVIImage
        alt={product.name}
        src={
          product.imageUrl ||
          product.images?.[0]?.path ||
          '/images/product-placeholder.svg'
        }
        style={{
          width: '100%',
          height: 132,
          marginBottom: 8
        }}
      />
      <SHighlightText
        value={product.name}
        searchQuery={searchQuery}
        weight="semibold"
        size="sm"
        noLineClamp
        fontColor={TextColor.Black}
      />
      {handleCheck && (
        <Box
          style={{ position: 'absolute', top: 10, right: 10 }}
          onClick={e => e.stopPropagation()}>
          <SCheckbox
            icon={<RoundCheckboxUnselect />}
            checkedIcon={<RoundCheckboxSelected />}
            onChange={e => {
              e.stopPropagation();
              handleCheck(e.target.checked, [product]);
            }}
            checked={checked}
            disabled={checkboxDisabled?.(product)}
          />
        </Box>
      )}
      <Box display="flex" flexDirection="column" py={2}>
        <SText
          title={`SKU: ${product.sku}`}
          size="sm"
          fontColor={TextColor.Grey}
        />
        {gridConfig?.stock?.show && (
          <Stock
            variant="body2"
            style={{ minWidth: 'max-content' }}
            inventory={product.inventory}
            isDropship={product.dropship}
            selectedCustomer={filters?.customer?.value}
            onClick={() => onStockClick(product)}
          />
        )}
        {gridConfig?.manufacturerLeadTime && (
          <SText
            title={`Lead time: ${product?.custom?.leadTime ? `${product?.custom?.leadTime} Days` : '-'}`}
            size="sm"
          />
        )}
      </Box>
      <Divider />
      <Box py="12px">
        <SText
          size="sm"
          title={`MFG: ${product?.vendor?.name || 'Not Available'}`}
        />
        <Box display="flex" alignItems="center" gridGap="4px">
          {product?.vendor?.vendorId && (
            <SText
              title={product.vendor.vendorId}
              size="sm"
              fontColor={TextColor.SlateGrey}
              noLineClamp
            />
          )}
          {product.privateLabel && <ProductLabel />}
          {product?.vendorHierarchy > 0 && (
            <ProductLabel type="strategic-vendor" />
          )}
          {product.promotions?.length > 0 && (
            <PromotionBadge onClick={() => onPromotionClick(product)} />
          )}
        </Box>
      </Box>
      <Divider />

      {priceType === ProductPriceType.Full && !!gridConfig?.pricing.length && (
        <BoxWithMargin>
          <MobileGridFullPrice
            fontSize="sm"
            product={product}
            loading={productPricesLoading}
            pricing={gridConfig?.pricing}
          />
          <Divider style={{ marginTop: 8 }} />
        </BoxWithMargin>
      )}
      {priceType === ProductPriceType.List && (
        <BoxWithMargin>
          <ListPrice product={product} fontSize="sm" />
          <Divider style={{ marginTop: 8 }} />
        </BoxWithMargin>
      )}

      {priceType === ProductPriceType.Get && filters?.customer?.value && (
        <BoxWithMargin>
          <Box display="flex" justifyContent="center" height={40}>
            <PriceButton
              price={product.price}
              onGetPrice={() => onGetProductPrice(product)}
            />
          </Box>
          <Divider style={{ marginTop: 8 }} />
        </BoxWithMargin>
      )}

      {priceType === ProductPriceType.GetFull && filters?.customer?.value && (
        <BoxWithMargin>
          <Box display="flex" justifyContent="center">
            {product.price ? (
              <MobileGridFullPrice
                fontSize="sm"
                product={product}
                loading={productPricesLoading}
                pricing={gridConfig?.pricing}
              />
            ) : (
              <SButton
                loading={singlePriceLoading}
                onClick={async e => {
                  e.stopPropagation();
                  setSinglePriceLoading(true);
                  await onGetProductPrice(product);
                  setSinglePriceLoading(false);
                }}
                style={{ width: 'max-content' }}
                variant="outlined">
                Get Price
              </SButton>
            )}
          </Box>
          <Divider style={{ marginTop: 8 }} />
        </BoxWithMargin>
      )}

      <BoxWithMargin>
        <Statuses product={product} />
      </BoxWithMargin>
      {aiSearchActive && (
        <AIProductMatch
          percentage={product?.percentage}
          reasoning={product?.reasoning}
        />
      )}
    </MobileCard>
  );
};
